/** Shared stock helpers — Centre Activité HUB-E. */

export const STOCK_SECTIONS = {
  OVERVIEW: 'overview',
  MOUVEMENTS: 'mouvements',
  ALERTES: 'alertes',
  TRANSFERTS: 'transferts',
};

export const MOTIF_LABELS = {
  achat_fournisseur: 'Achat fournisseur',
  retour_client: 'Retour client',
  correction_inventaire: 'Correction inventaire',
  transfert_entrant: 'Transfert entrant',
  reception_commande: 'Réception commande',
  reintegration_reparation: 'Réintégration réparation',
  vente_pos: 'Vente POS',
  consommation_reparation: 'Consommation réparation',
  perte_casse: 'Perte / casse',
  retour_fournisseur: 'Retour fournisseur',
  transfert_sortant: 'Transfert sortant',
  ajustement_inventaire: 'Ajustement inventaire',
};

export function magasinScopeLabel(magasinId) {
  if (!magasinId || magasinId === 'ALL') return 'Tous les magasins';
  return magasinId.toUpperCase();
}

export function movementTypeMeta(m) {
  if (m.type_mouvement === 'transfert') {
    return { label: 'Transfert', badge: 'rm-badge rm-badge--muted' };
  }
  if (m.type_mouvement === 'entree') {
    return { label: 'Entrée', badge: 'rm-badge rm-badge--success' };
  }
  if (m.motif === 'ajustement_inventaire' || m.motif === 'correction_inventaire') {
    return { label: 'Ajustement', badge: 'rm-badge rm-badge--muted' };
  }
  return { label: 'Sortie', badge: 'rm-badge rm-badge--danger' };
}

export function formatMovementDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function matchesMovementSearch(m, search) {
  if (!search?.trim()) return true;
  const q = search.trim().toLowerCase();
  const haystack = [
    m.reference,
    m.designation,
    m.motif,
    MOTIF_LABELS[m.motif],
    m.admin_username,
    m.from_magasin_id,
    m.to_magasin_id,
    m.magasin_id,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function matchesArticleSearch(a, search) {
  if (!search?.trim()) return true;
  const q = search.trim().toLowerCase();
  return [a.reference, a.designation, a.ean, a.magasin_id]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q);
}

export function classifyAlerts(articles = []) {
  const ruptures = [];
  const faible = [];
  articles.forEach((a) => {
    const qty = Number(a.quantite ?? 0);
    const seuil = Number(a.seuil_alerte ?? 3);
    const mag = (a.magasin_id || 'M1').toUpperCase();
    const item = { ...a, magasin_id: mag };
    if (qty <= 0) ruptures.push(item);
    else if (qty <= seuil) faible.push(item);
  });
  ruptures.sort((a, b) => a.quantite - b.quantite);
  faible.sort((a, b) => a.quantite - b.quantite);
  return { ruptures, faible };
}

export function computeMagasinAlertCounts(articles = []) {
  const counts = { M1: { ruptures: 0, faible: 0 }, M2: { ruptures: 0, faible: 0 } };
  articles.forEach((a) => {
    const mag = (a.magasin_id || 'M1').toUpperCase();
    if (!counts[mag]) counts[mag] = { ruptures: 0, faible: 0 };
    const qty = Number(a.quantite ?? 0);
    const seuil = Number(a.seuil_alerte ?? 3);
    if (qty <= 0) counts[mag].ruptures += 1;
    else if (qty <= seuil) counts[mag].faible += 1;
  });
  return counts;
}

export function computeTopSold(movements = [], limit = 5) {
  const map = {};
  movements.forEach((m) => {
    const isSale = m.motif === 'vente_pos' || (m.type_mouvement === 'sortie' && m.motif === 'vente_pos');
    if (!isSale) return;
    const ref = m.reference;
    if (!ref) return;
    if (!map[ref]) {
      map[ref] = {
        reference: ref,
        designation: m.designation || ref,
        magasin_id: m.magasin_id || m.from_magasin_id || 'M1',
        quantite: 0,
      };
    }
    map[ref].quantite += Number(m.quantite || 0);
  });
  return Object.values(map).sort((a, b) => b.quantite - a.quantite).slice(0, limit);
}

export function computeTopActive(movements = [], limit = 5) {
  const map = {};
  movements.forEach((m) => {
    const ref = m.reference;
    if (!ref) return;
    if (!map[ref]) {
      map[ref] = {
        reference: ref,
        designation: m.designation || ref,
        magasin_id: m.magasin_id || m.from_magasin_id || 'M1',
        count: 0,
      };
    }
    map[ref].count += 1;
  });
  return Object.values(map).sort((a, b) => b.count - a.count).slice(0, limit);
}

export function activiteStockLinkFromFilters(filters) {
  const params = new URLSearchParams({ tab: 'stock' });
  if (filters?.search) params.set('search', filters.search);
  if (filters?.date_debut) params.set('date_debut', filters.date_debut);
  if (filters?.date_fin) params.set('date_fin', filters.date_fin);
  if (filters?.magasin_id) params.set('magasin_id', filters.magasin_id);
  if (filters?.period) params.set('period', filters.period);
  return `/activite?${params.toString()}`;
}
